/**
 * These functions were originally copied from https://github.com/remix-run/react-router/
 */
import * as React from 'react'
import { useHistory, useLocation } from 'react-router'

interface NavigateOptions {
    /** Replace the current entry in the history stack instead of pushing a new one */
    replace?: boolean;
    /** Adds persistent client side routing state to the next location */
    state?: any;
    /** If you are using {@link https://api.reactrouter.com/v7/functions/react_router.ScrollRestoration.html <ScrollRestoration>}, prevent the scroll position from being reset to the top of the window when navigating */
    preventScrollReset?: boolean;
    /** Defines the relative path behavior for the link. "route" will use the route hierarchy so ".." will remove all URL segments of the current route pattern while "path" will use the URL path so ".." will remove one URL segment. */
    relative?: 'route' | 'path';
    /** Wraps the initial state update for this navigation in a {@link https://react.dev/reference/react-dom/flushSync ReactDOM.flushSync} call instead of the default {@link https://react.dev/reference/react/startTransition React.startTransition} */
    flushSync?: boolean;
    /** Enables a {@link https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API View Transition} for this navigation by wrapping the final state update in `document.startViewTransition()`. If you need to apply specific styles for this view transition, you will also need to leverage the {@link https://api.reactrouter.com/v7/functions/react_router.useViewTransitionState.html useViewTransitionState()} hook.  */
    viewTransition?: boolean;
}

type ParamKeyValuePair = [string, string];

type URLSearchParamsInit =
    | string
    | ParamKeyValuePair[]
    | Record<string, string | string[]>
    | URLSearchParams;

type SetURLSearchParams = (
        nextInit?:
            | URLSearchParamsInit
            | ((prev: URLSearchParams) => URLSearchParamsInit),
        navigateOpts?: NavigateOptions
    ) => void;

/**
    Creates a URLSearchParams object using the given initializer.

    This is identical to `new URLSearchParams(init)` except it also
    supports arrays as values in the object form of the initializer
    instead of just strings. This is convenient when you need multiple
    values for a given key, but don't want to use an array initializer.

    For example, instead of:

    ```tsx
    let searchParams = new URLSearchParams([
        ['sort', 'name'],
        ['sort', 'price']
    ]);
    ```
    you can do:

    ```
    let searchParams = createSearchParams({
        sort: ['name', 'price']
    });
    ```

    @category Utils
 */
function createSearchParams(
    init: URLSearchParamsInit = ""
): URLSearchParams {
    return new URLSearchParams(
        typeof init === "string" ||
        Array.isArray(init) ||
        init instanceof URLSearchParams
            ? init
            : Object.keys(init).reduce((memo, key) => {
                let value = init[key];
                return memo.concat(
                    Array.isArray(value) ? value.map((v) => [key, v]) : [[key, value]]
                );
                }, [] as ParamKeyValuePair[])
    );
}

function getSearchParamsForLocation(
    locationSearch: string,
    defaultSearchParams: URLSearchParams | null
) {
    let searchParams = createSearchParams(locationSearch);
  
    if (defaultSearchParams) {
        // Use `defaultSearchParams.forEach(...)` here instead of iterating of
        // `defaultSearchParams.keys()` to work-around a bug in Firefox related to
        // web extensions. Relevant Bugzilla tickets:
        // https://bugzilla.mozilla.org/show_bug.cgi?id=1414602
        // https://bugzilla.mozilla.org/show_bug.cgi?id=1023984
        defaultSearchParams.forEach((_, key) => {
            if (!searchParams.has(key)) {
                defaultSearchParams.getAll(key).forEach((value) => {
                    searchParams.append(key, value);
                });
            }
        });
    }
  
    return searchParams;
  }

/**
 * Returns a tuple of the current URL's {@link URLSearchParams} and a function to update them. Setting the search params causes a navigation.

  ```tsx
  export function SomeComponent() {
    const [searchParams, setSearchParams] = useSearchParams()
    // ...
  }
 * @link https://github.com/remix-run/react-router/blob/3cb7b48ca1b0a9712e017756fec1617eee815c88/packages/react-router/lib/dom/lib.tsx#L1391
 * @param defaultInit 
 * @returns 
 */
export function useSearchParams(
    defaultInit?: URLSearchParamsInit
): [URLSearchParams, SetURLSearchParams] {  
    let defaultSearchParamsRef = React.useRef(createSearchParams(defaultInit))
    let hasSetSearchParamsRef = React.useRef(false)
  
    let location = useLocation()
    let searchParams = React.useMemo(
        () =>
            // Only merge in the defaults if we haven't yet called setSearchParams.
            // Once we call that we want those to take precedence, otherwise you can't
            // remove a param with setSearchParams({}) if it has an initial value
            getSearchParamsForLocation(
                location.search,
                hasSetSearchParamsRef.current ? null : defaultSearchParamsRef.current
            ),
        [location.search]
    )
  
    let history = useHistory()
    let setSearchParams = React.useCallback<SetURLSearchParams>(
        (nextInit, navigateOptions) => {
            const newSearchParams = createSearchParams(
                typeof nextInit === 'function' ? nextInit(searchParams) : nextInit
            )
            hasSetSearchParamsRef.current = true
            history.replace('?' + newSearchParams, navigateOptions)
        },
        [history, searchParams]
    )
  
    return [searchParams, setSearchParams]
}

export default useSearchParams
